// 261005 E3-P4b1 — thẻ khối chặng ① Nguồn: lệnh + lý do · ảnh hưởng (quyển cần quét lại / tập cần chia lại) · tải lại: độ dài cũ → mới, đầu/cuối, cảnh báo ·
// Xem thử / Áp / Khôi phục bản trước. KHÔNG có "Áp lại" (sau khôi phục chỉ "Xem thử lại"). Mọi quyết định hiển thị ở `sourceBlockView.ts` (hàm thuần, có jest); file này chỉ vẽ.
import React from 'react';
import { Alert, Collapse, Popconfirm, Tooltip, Typography } from 'antd';
import Button from '../WbButton';
import type { AssistantBlock } from '../../../../types/adaptationAssistant';
import { sourceCardView } from './sourceBlockView';
import type { SourceTrimRow } from './sourceBlockView';
import { disabledTip } from './assistantView';
import { BlockBody, BlockFoot, BlockShell } from './BlockShell';
import type { TimelineHandlers } from './AssistantTimeline'; // chỉ kiểu — không vòng phụ thuộc lúc chạy

const { Text } = Typography;

interface Props extends TimelineHandlers {
  block: AssistantBlock;
  headModifiedAt?: string | null;
  busy: boolean;
  acting: string | null;
  busyReason?: string | null;
}

const Excerpt: React.FC<{ label: string; text: string }> = ({ label, text }) => (text ? (
  <div style={{ fontSize: 13, margin: '2px 0', wordBreak: 'break-word' }}><Text type="secondary">{label}: </Text><span style={{ whiteSpace: 'pre-wrap' }}>{text}</span></div>
) : null);

/** F26 — một chương trong thẻ "Cắt rác": độ dài trước → sau, lý do, đoạn bị cắt dạng gạch bỏ (−) như thẻ đề xuất khác. */
const TrimRow: React.FC<{ row: SourceTrimRow }> = ({ row }) => (
  <div style={{ margin: '4px 0', fontSize: 13, wordBreak: 'break-word' }}>
    <Text strong style={{ fontSize: 13 }}>{row.label}</Text><Text type="secondary" style={{ fontSize: 13 }}> · {row.lengthLine} ký tự</Text>
    {row.reasons.map((r, i) => <div key={i}><Text type="secondary" style={{ fontSize: 13 }}>{r}</Text></div>)}
    {row.headCut && <span className="wb-diff-del" style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{`− Đầu: ${row.headCut.trim()}`}</span>}
    {row.tailCut && <span className="wb-diff-del" style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{`− Cuối: ${row.tailCut.trim()}`}</span>}
  </div>
);

const SourceBlockCard: React.FC<Props> = ({ block, headModifiedAt, busy, acting, busyReason, onPreview, onApply, onUndo, onDismiss }) => {
  const v = sourceCardView(block, busy || !!acting, headModifiedAt, busyReason);
  const why = busy && busyReason ? busyReason : undefined;
  return (
    <BlockShell>
      <BlockBody title={v.title ?? block.title} tone={v.tone} stateLabel={v.headline} running={v.phase === 'running'} chip={block.command?.op === 'reload' ? 'Tải trang nguồn' : null}>
      {v.opLine && <div style={{ fontSize: 13, margin: '4px 0', wordBreak: 'break-word' }}>{v.opLine}</div>}
      {v.detail && <div style={{ fontSize: 13, color: 'var(--wb-text-2)', margin: '4px 0', wordBreak: 'break-word' }}>{v.detail}</div>}
      {v.reload && (
        <div style={{ margin: '4px 0' }}>
          <Text style={{ fontSize: 13 }}>{v.reload.lengthLine}</Text>
          <Excerpt label="Đầu" text={v.reload.head} />
          <Excerpt label="Cuối" text={v.reload.tail} />
          {v.reload.warning && <Alert style={{ marginTop: 4 }} type="warning" showIcon message={v.reload.warning} />}
          {v.reload.problems.length > 0 && <Alert style={{ marginTop: 4 }} type="warning" showIcon message="Trang tải về trông không giống một chương" description={v.reload.problems.join(' · ')} />}
        </div>
      )}
      {v.trim && (
        <div style={{ margin: '4px 0' }}>
          <Text style={{ fontSize: 13 }}>{v.trim.totalLine}</Text>
          {v.trim.skippedLine && <Alert style={{ marginTop: 4 }} type="warning" showIcon message={v.trim.skippedLine} />}
          {v.trim.collapsed ? (
            <Collapse
              ghost size="small"
              items={[{ key: 'rows', label: `Xem ${v.trim.rows.length} chương bị cắt`, children: <div>{v.trim.rows.map((r) => <TrimRow key={r.key} row={r} />)}</div> }]}
            />
          ) : v.trim.rows.map((r) => <TrimRow key={r.key} row={r} />)}
        </div>
      )}
      {v.impact.length > 0 && <Alert style={{ margin: '6px 0' }} type="info" showIcon message="Ảnh hưởng" description={v.impact.map((l, i) => <div key={i} style={{ fontSize: 13 }}>{l}</div>)} />}
      </BlockBody>
      <BlockFoot>
        {v.canPreview && <Button size="small" type="text" loading={acting === `preview-${block.id}`} onClick={() => onPreview(block)}>{v.previewLabel}</Button>}
        {!v.canPreview && why && (v.phase === 'proposed' || v.phase === 'restored' || v.phase === 'failed' || v.phase === 'stale') && (
          <Tooltip title={why}><Button size="small" type="text" disabled>{v.previewLabel}</Button></Tooltip>
        )}
        {v.phase === 'ready' && !v.noChange && (
          <Popconfirm
            title="Áp đề xuất này?" description="Ghi vào chương thô (có phiên bản — hoàn tác được ngay sau đó); không tự quét hay chia lại."
            okText="Áp" cancelText="Thôi" onConfirm={() => onApply(block)} disabled={!v.canApply}
          >
            <Tooltip title={disabledTip(v.canApply, why)}><Button size="small" type="primary" disabled={!v.canApply} loading={acting === `apply-${block.id}`}>Áp</Button></Tooltip>
          </Popconfirm>
        )}
        {v.phase === 'applied' && (
          <Tooltip title={disabledTip(v.canUndo, v.undoReason ?? v.undoHint)}>
            <span>
              <Popconfirm title="Khôi phục bản trước?" description={v.undoHint} okText="Khôi phục" cancelText="Thôi" onConfirm={() => onUndo(block)} disabled={!v.canUndo}>
                <Button size="small" type="text" disabled={!v.canUndo} loading={acting === `undo-${block.id}`}>Khôi phục bản trước</Button>
              </Popconfirm>
            </span>
          </Tooltip>
        )}
        {v.canDismiss && <Button size="small" type="text" loading={acting === `dismiss-${block.id}`} onClick={() => onDismiss(block)}>Bỏ</Button>}
      </BlockFoot>
    </BlockShell>
  );
};

export default SourceBlockCard;
